import type {
  BuilderContextValue,
  BuilderMessage,
  PendingComposioExec,
  StructuredIntent,
  WorkflowPlan,
} from "../builder-types";
import { isComposioApprovalSettled, composioApprovalIdFromMessage } from "@/shared/builder-session";
import { setChatAwaitingInput, setChatStreaming } from "@/features/chat/multi/signals/chat-signals";

export const GRAPH_EVENTS = new Set([
  "node-added",
  "node-updated",
  "graph-relayout",
  "edge-added",
  "edges-sync",
  "complete",
]);

/**
 * Sidebar / toolbar "Building…" — live canvas materialization only.
 * Stuck phase or leftover nodes must not keep Building after the agent parks/idles.
 */
export function isLiveWorkflowBuilding(phase: string | null | undefined, busy: boolean): boolean {
  return (phase === "building" || phase === "testing") && Boolean(busy);
}

/** True when an Approve / HITL card owns the UI — hide Working… / Stop. */
export function isBuilderReviewParked(builder: {
  phase: string;
  intent?: { status?: string } | null;
  pendingUserQuestion?: unknown;
  pendingComposioExecQueue?: Array<{ approvalId?: string }>;
  pendingRuntimeInput?: unknown;
  messages?: BuilderMessage[];
  nodes?: { length: number } | unknown[];
}): boolean {
  if (builder.pendingUserQuestion) return true;
  if (builder.pendingRuntimeInput) return true;
  const openComposio = (builder.pendingComposioExecQueue ?? []).filter(
    (pending) => !isComposioApprovalSettled(builder.messages ?? [], pending.approvalId),
  );
  if (openComposio.length > 0) return true;
  const hasNodes = Array.isArray(builder.nodes)
    ? builder.nodes.length > 0
    : Boolean(builder.nodes && "length" in builder.nodes && builder.nodes.length > 0);
  if (
    (builder.phase === "awaiting approval" && !hasNodes) ||
    builder.phase === "awaiting intent approval" ||
    builder.phase === "awaiting input"
  ) {
    return true;
  }
  if (builder.intent?.status === "confirmed") return false;
  return builder.intent?.status === "awaiting_confirmation";
}

/** Normalize plan payloads from SSE `plan` or HTTP plan-decision responses. */
export function asWorkflowPlan(data: unknown): WorkflowPlan | null {
  if (!data || typeof data !== "object") return null;
  const record = data as Record<string, unknown>;
  if (record.type === "json" && record.value && typeof record.value === "object") {
    return asWorkflowPlan(record.value);
  }
  if (
    Array.isArray(record.steps) ||
    typeof record.title === "string" ||
    typeof record.name === "string" ||
    (Array.isArray(record.fieldMappings) && record.fieldMappings.length > 0) ||
    (record.validationPlan &&
      typeof record.validationPlan === "object" &&
      Array.isArray((record.validationPlan as { scenarios?: unknown }).scenarios) &&
      (record.validationPlan as { scenarios: unknown[] }).scenarios.length > 0)
  ) {
    return data as WorkflowPlan;
  }
  const nested = record.plan;
  if (nested && typeof nested === "object") {
    return asWorkflowPlan(nested);
  }
  return null;
}

function isStructuredIntent(value: unknown): boolean {
  if (!value || typeof value !== "object") return false;
  const record = value as StructuredIntent;
  return (
    typeof record.id === "string" &&
    record.intent != null &&
    typeof record.intent === "object" &&
    Array.isArray(record.intent.unknowns)
  );
}

export function asStructuredIntent(data: unknown): StructuredIntent | null {
  if (!isStructuredIntent(data)) return null;
  return data as StructuredIntent;
}

function sameIntentFamily(a: StructuredIntent, b: StructuredIntent): boolean {
  return (
    a.id === b.id ||
    a.supersedes === b.id ||
    b.supersedes === a.id ||
    a.id === b.supersedes ||
    b.id === a.supersedes
  );
}

/** True when a late awaiting_* snapshot/SSE must not undo a local Approve. */
export function rejectsAwaitingIntentDowngrade(
  local: StructuredIntent | null | undefined,
  incoming: StructuredIntent,
): boolean {
  return Boolean(
    local?.status === "confirmed" &&
    incoming.status !== "confirmed" &&
    sameIntentFamily(local, incoming),
  );
}

function planHasSteps(plan: unknown): plan is WorkflowPlan {
  return Boolean(
    plan &&
    typeof plan === "object" &&
    Array.isArray((plan as WorkflowPlan).steps) &&
    (plan as WorkflowPlan).steps!.length > 0,
  );
}

/**
 * Prefer the painted card payload, fill gaps from session.plan (summary / steps).
 * Prevents an empty header when the transcript row lagged behind the session SoT.
 */
export function mergePlanForDisplay(
  card: WorkflowPlan | null | undefined,
  sessionPlan: WorkflowPlan | null | undefined,
): WorkflowPlan | null {
  if (!card && !sessionPlan) return null;
  if (!card) return sessionPlan ?? null;
  if (!sessionPlan) return card;
  const summary =
    (typeof card.summary === "string" && card.summary.trim()) ||
    (typeof sessionPlan.summary === "string" && sessionPlan.summary.trim()) ||
    undefined;
  const steps = Array.isArray(card.steps) && card.steps.length > 0 ? card.steps : sessionPlan.steps;
  return {
    ...sessionPlan,
    ...card,
    ...(summary ? { summary } : {}),
    ...(steps ? { steps } : {}),
    fieldMappings:
      Array.isArray(card.fieldMappings) && card.fieldMappings.length > 0
        ? card.fieldMappings
        : sessionPlan.fieldMappings,
    assumptions: card.assumptions?.length ? card.assumptions : sessionPlan.assumptions,
    workarounds: card.workarounds?.length ? card.workarounds : sessionPlan.workarounds,
    validationPlan: card.validationPlan?.scenarios?.length
      ? card.validationPlan
      : sessionPlan.validationPlan?.scenarios?.length
        ? sessionPlan.validationPlan
        : (card.validationPlan ?? sessionPlan.validationPlan),
  };
}

function lastIndexOfType(messages: BuilderMessage[], type: BuilderMessage["type"]): number {
  for (let index = messages.length - 1; index >= 0; index -= 1) {
    if (messages[index]?.type === type) return index;
  }
  return -1;
}

/**
 * Live SSE: update matching intent in place, otherwise append.
 * Does not reshuffle the transcript.
 */
export function paintIntentCard(builder: BuilderContextValue, intent: StructuredIntent) {
  if (rejectsAwaitingIntentDowngrade(builder.intent, intent)) return;

  builder.setIntent(intent);
  if (intent.intent.unknowns.length > 0) return;

  builder.setBuildStalled(false);
  builder.setError(null);

  if (intent.status !== "confirmed") {
    if (
      intent.status === "awaiting_confirmation" ||
      (builder.phase !== "awaiting approval" &&
        builder.phase !== "building" &&
        builder.phase !== "testing" &&
        builder.phase !== "complete")
    ) {
      builder.setPhase("awaiting intent approval");
    }
    builder.setBusy(false);
    const sessionId = builder.sessionId?.trim();
    if (sessionId) {
      setChatStreaming(sessionId, false);
      setChatAwaitingInput(sessionId, true);
    }
  }

  const idx = builder.messages.findIndex(
    (message) =>
      message.type === "intent" &&
      (message.details as StructuredIntent | undefined)?.id === intent.id,
  );
  if (idx >= 0) {
    const current = builder.messages[idx]!;
    const next = [...builder.messages];
    next[idx] = {
      ...current,
      text: current.text || "Intent ready for approval.",
      details: intent,
    };
    builder.restoreMessages(next);
    return;
  }

  // Same run / supersede: update the latest intent in place instead of appending a
  // second card after later Answers confirmed rows.
  const lastIntentIdx = lastIndexOfType(builder.messages, "intent");
  if (lastIntentIdx >= 0) {
    const prior = asStructuredIntent(builder.messages[lastIntentIdx]?.details);
    // Confirm replaces pending-* with a new UUID and may omit supersedes —
    // still fold onto the open card so Approve does not stay armed.
    const sameSessionConfirm =
      intent.status === "confirmed" &&
      prior &&
      prior.status !== "confirmed" &&
      Boolean(prior.sessionId) &&
      prior.sessionId === intent.sessionId;
    if (prior && (sameIntentFamily(prior, intent) || sameSessionConfirm)) {
      const current = builder.messages[lastIntentIdx]!;
      const next = [...builder.messages];
      next[lastIntentIdx] = {
        ...current,
        text: current.text || "Intent ready for approval.",
        details: sameSessionConfirm
          ? { ...intent, id: prior.id, supersedes: intent.supersedes ?? prior.supersedes }
          : intent,
      };
      builder.restoreMessages(next);
      return;
    }
  }

  builder.appendMessage("intent", "Intent ready for approval.", intent);
}

/**
 * Live SSE: update the latest plan card in place, otherwise append.
 * Does not reshuffle earlier messages.
 */
export function paintPlanCard(builder: BuilderContextValue, plan: WorkflowPlan) {
  const existingIdx = lastIndexOfType(builder.messages, "plan");
  const existing = existingIdx >= 0 ? builder.messages[existingIdx] : undefined;
  const existingPlan = existing?.details as WorkflowPlan | undefined;
  // Always merge so a thin SSE update cannot wipe plan.summary off the card.
  const details =
    mergePlanForDisplay(plan, existingPlan) ?? (planHasSteps(plan) ? plan : existingPlan) ?? plan;

  const alreadyBuilding =
    builder.phase === "building" ||
    builder.phase === "testing" ||
    builder.phase === "complete" ||
    builder.nodes.length > 0;

  builder.setPlan(details);
  builder.setBuildStalled(false);
  builder.setError(null);

  // After Build (or once nodes exist), late plan paints must not re-park review.
  if (!alreadyBuilding) {
    builder.setBusy(false);
    const sessionId = builder.sessionId?.trim();
    if (sessionId) {
      setChatStreaming(sessionId, false);
      setChatAwaitingInput(sessionId, true);
    }
    if (
      builder.phase !== "building" &&
      builder.phase !== "testing" &&
      builder.phase !== "complete"
    ) {
      builder.setPhase("awaiting approval");
    }
  }

  if (existingIdx >= 0) {
    const current = builder.messages[existingIdx]!;
    const next = [...builder.messages];
    next[existingIdx] = {
      ...current,
      text: "Plan ready for approval.",
      details,
    };
    builder.restoreMessages(next);
    return;
  }

  if (alreadyBuilding) return;
  builder.appendMessage("plan", "Plan ready for approval.", details);
}

export function composioDecisionMessageId(approvalId: string): string {
  return `composio-decision-${approvalId.trim()}`;
}

/** Upsert one Composio decision card by approvalId — no transcript reshuffle. */
export function paintComposioDecision(
  builder: BuilderContextValue,
  options: { approved: boolean; pending: PendingComposioExec },
): void {
  const approvalId = options.pending.approvalId?.trim();
  if (!approvalId) return;

  const id = composioDecisionMessageId(approvalId);
  const nextMessage: BuilderMessage = {
    id,
    type: "user",
    text: options.approved
      ? `Approved ${options.pending.toolSlug}.`
      : `Rejected ${options.pending.toolSlug}.`,
    details: {
      kind: "composio-decision" as const,
      approved: options.approved,
      pending: { ...options.pending, approvalId },
    },
  };

  const existingIdx = builder.messages.findIndex(
    (message) => message.id === id || composioApprovalIdFromMessage(message) === approvalId,
  );
  if (existingIdx >= 0) {
    builder.restoreMessages(
      builder.messages.map((message, index) => (index === existingIdx ? nextMessage : message)),
    );
    return;
  }
  builder.restoreMessages([...builder.messages, nextMessage]);
}

export function isDraftSavedMessage(message: BuilderMessage): boolean {
  return (message.details as { kind?: string } | undefined)?.kind === "draft-saved";
}

export function paintDraftSavedCard(builder: BuilderContextValue) {
  if (builder.messages.some(isDraftSavedMessage)) return;
  builder.appendMessage("status", "Draft saved", { kind: "draft-saved" });
}

/** submit_plan tool-call args — field mappings exist before the plan SSE. */
export function planDraftFromSubmitPlanToolData(data: unknown): WorkflowPlan | null {
  if (!data || typeof data !== "object") return null;
  const record = data as { args?: unknown; input?: unknown; tool?: string };
  return asWorkflowPlan(record.args ?? record.input ?? data);
}

export function connectLinkFromToolResult(data: unknown): string | null {
  if (!data || typeof data !== "object") return null;
  const payload = data as {
    tool?: string;
    result?: { redirectUrl?: string; connectionRequestId?: string };
  };
  if (payload.tool !== "get_connect_link") return null;
  const url = payload.result?.redirectUrl?.trim();
  return url || null;
}

/** Toolkit from the most recent get_connect_link tool-call in the transcript. */
export function connectToolkitFromMessages(
  messages: Array<{ type: string; details?: unknown }>,
): string | null {
  for (let i = messages.length - 1; i >= 0; i -= 1) {
    const item = messages[i];
    if (item.type !== "tool") continue;
    const details = item.details as
      { tool?: string; phase?: string; args?: { toolkit?: string } } | undefined;
    if (details?.tool !== "get_connect_link") continue;
    if (details.phase === "result") continue;
    const toolkit = details.args?.toolkit?.trim().toLowerCase();
    if (toolkit) return toolkit;
  }
  return null;
}

/**
 * Composio mint a new URL on every get_connect_link call. Without this,
 * each agent retry appends another "Connect this platform…" bubble.
 */
export function alreadyShowingConnectLink(
  messages: Array<{ type: string; details?: unknown }>,
  toolkit: string | null,
): boolean {
  return messages.some((item) => {
    if (item.type !== "assistant") return false;
    const details = item.details as
      { kind?: string; toolkit?: string; redirectUrl?: string } | undefined;
    if (details?.kind !== "composio-connect") return false;
    if (toolkit && details.toolkit) return details.toolkit === toolkit;
    return Boolean(details.redirectUrl);
  });
}

export function serviceError(error: unknown): string {
  const detail = error instanceof Error ? error.message : "The builder request failed.";
  // FlowOps never paints proxy statusText; keep the soft "Request failed" as-is.
  if (/^Request failed$/i.test(detail.trim())) return detail;
  if (
    /fetch|network|unavailable|503|connect|ECONNRESET|socket hang up|Internal Server Error/i.test(
      detail,
    )
  ) {
    return "Request failed";
  }
  return detail;
}

/** FlowOps HITL resume: stale/finished durable run — not a user-facing hard failure. */
export function isBenignHitlResumeFailure(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error ?? "");
  return (
    /No parked durable run/i.test(message) ||
    /No registry entry found for run/i.test(message) ||
    /No snapshot found for this workflow run/i.test(message) ||
    /Cannot resume/i.test(message) ||
    /workflow (run|step)[\s\S]*was not suspended/i.test(message) ||
    /because it is not suspended/i.test(message)
  );
}
